import { RhButton, RhInputFormik, RhLabel, RhToast } from "@rhythm-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";

import api from "@/services/api";

import { useScreenDetector } from "@/hooks/useScreenDetector";

import CheckBoxInputFormik from "@/components/formikInput/CheckBoxInputFormik";
import { Dialog } from "@/components/ui/Dialog";
import Button from "@/components/utils/Button";

const CreateJobRevokeAppDialog = ({
  isRevokeApplicationForJobIdDialogOpen,
  setIsRevokeApplicationForJobIdDialogOpen,
}: {
  isRevokeApplicationForJobIdDialogOpen: number | null;
  setIsRevokeApplicationForJobIdDialogOpen: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}) => {
  const jobID = isRevokeApplicationForJobIdDialogOpen;
  const initialForm: {
    job_requirement: number | null;
    reasons: [];
    other_reason: string;
  } = {
    job_requirement: jobID,
    reasons: [],
    other_reason: "",
  };

  const { data: candidateRevokeJobApplicationReasons } = useQuery({
    queryKey: ["revoke-job-application-reasons"],
    queryFn: () =>
      api.company.getCandidateRevokeJobApplicationReasons({
        pagination: false,
      }),
    select: (data) =>
      data.data.map((item: { name: string; id: number }) => ({
        label: item.name,
        value: item.id.toString(),
      })),
  });

  const queryClient = useQueryClient();

  const { isTablet: isShowMobileView } = useScreenDetector();

  const postRevokeCandidateJobApplicationMutation = useMutation({
    mutationFn: (data: {
      job_requirement: number;
      reasons: number[];
      other_reason: string;
    }) => api.company.postRevokeCandidateJobApplication(data),
    onSuccess() {
      setIsRevokeApplicationForJobIdDialogOpen(null);

      if (isShowMobileView) {
        RhToast.success("Job application withdrawn", {
          position: "top-right",
          className: "top-[56px]",
        });
      } else {
        RhToast.success("Job application withdrawn", {
          position: "bottom-right",
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["job", { id: jobID }],
      });
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });
    },
  });

  const validationSchema = Yup.object().shape({
    reasons: Yup.array(),
    other_reason: Yup.string().when("reasons", {
      is: (reasons: number[]) => reasons.length === 0,
      then: Yup.string().required(
        "Select at least one checkbox or provide a reasom"
      ),
    }),
  });

  const onSubmitHandler = async (data: {
    job_requirement: number | null;
    reasons: [];
    other_reason: string;
  }) => {
    const jobId = data.job_requirement;
    if (jobId === null) return;

    postRevokeCandidateJobApplicationMutation.mutate({
      ...data,
      job_requirement: jobId,
    });
  };

  return (
    <Dialog
      isOpen={isRevokeApplicationForJobIdDialogOpen !== null}
      className="w-[550px]"
    >
      <Formik
        initialValues={initialForm}
        onSubmit={onSubmitHandler}
        validationSchema={validationSchema}
      >
        {({ errors }) => (
          <Form className="flex flex-col [&>label]:mt-tsm">
            <>
              <div className="flex items-center justify-between lg:mb-tpico">
                <h2 className="text-lg lg:text-inherit">Revoke Application</h2>
              </div>

              <RhLabel
                required
                className="body-small lg:text-inherit !mb-tm1 lg:mb-0 !mt-tpico"
              >
                Tell us reasons for revoking the application
              </RhLabel>

              {candidateRevokeJobApplicationReasons && (
                <>
                  <CheckBoxInputFormik
                    name="reasons"
                    options={candidateRevokeJobApplicationReasons}
                    alignment="Vertical"
                  />
                  <RhInputFormik
                    className="mt-tnano"
                    type="text"
                    placeholder="Or mention other reason"
                    block
                    name="other_reason"
                  />
                  <p className=" text-red-500 caps">{errors.reasons}</p>
                </>
              )}

              <div className="mb-tsm" />

              <div className="flex justify-end gap-tnano lg:gap-tsm flex-col-reverse lg:flex-row">
                <RhButton
                  layout="link"
                  variant="secondary"
                  onClick={() => {
                    setIsRevokeApplicationForJobIdDialogOpen(null);
                  }}
                >
                  No, Cancel
                </RhButton>
                <Button
                  type="submit"
                  loading={postRevokeCandidateJobApplicationMutation.isLoading}
                >
                  Yes, Revoke
                </Button>
              </div>
            </>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateJobRevokeAppDialog;
